import React, { useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Circles from "../Circles/Circles";
import TextContainer from "../TextContainer/TextContainer";
import LogoLayer from "../LogoLayer/LogoLayer";
import { getAdjustedLeft } from "../../logoUtils";

import logoLayer01 from "../../styles/Layers/logoLayer01.svg";
import logoLayer02 from "../../styles/Layers/logoLayer02.svg";
import logoLayer03 from "../../styles/Layers/logoLayer03.svg";
import logoLayer04 from "../../styles/Layers/logoLayer04.svg";
import logoLayer05 from "../../styles/Layers/logoLayer05.svg";
import logoLayer06 from "../../styles/Layers/logoLayer06.svg";

const BASE_POSITION = 40.8;
const LAYER_OFFSET = 1.65;

const Logo: React.FC = () => {
  const [selectedLayer, setSelectedLayer] = useState<number | null>(null);
  const [transitionPhase, setTransitionPhase] = useState<
    "default" | "newPosition"
  >("default");
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const navigate = useNavigate();

  const layers = [
    {
      src: logoLayer01,
      alt: "Logo Layer 1",
      left: BASE_POSITION + LAYER_OFFSET * 0,
      zIndex: 6,
      title: "Web Development",
      color: 0,
      description: ["Keep On Keeping On!"],
      href: "/WebDevelopment",
    },
    {
      src: logoLayer02,
      alt: "Logo Layer 2",
      left: BASE_POSITION + LAYER_OFFSET * 1,
      zIndex: 5,
      title: "Photography",
      color: 1,
      description: [
        "Take a look... Then think for yourself.",
      ],
      href: "/Photography",
    },
    {
      src: logoLayer03,
      alt: "Logo Layer 3",
      left: BASE_POSITION + LAYER_OFFSET * 2,
      zIndex: 4,
      title: "Design",
      color: 2,
      description: [
        "Try to create something beautiful today.",
      ],
      href: "/Design",
    },
    {
      src: logoLayer04,
      alt: "Logo Layer 4",
      left: BASE_POSITION + LAYER_OFFSET * 3,
      zIndex: 3,
      title: "Audio Composition",
      color: 3,
      description: [
        "Nothing to see here..."
      ],
      href: "/AudioComposition",
    },
    {
      src: logoLayer05,
      alt: "Logo Layer 5",
      left: BASE_POSITION + LAYER_OFFSET * 4,
      zIndex: 2,
      title: "Craftmanship",
      color: 4,
      description: [
        "You won't believe your hands, till you start.",
      ],
      href: "/Craftmanship",
    },
    {
      src: logoLayer06,
      alt: "Logo Layer 6",
      left: BASE_POSITION + LAYER_OFFSET * 5,
      zIndex: 1,
      title: "About Me",
      color: 5,
      description: [
        "Big Brother is watching you.",
      ],
      href: "/AboutMe",
    },
  ];

  const handleLayerClick = (index: number) => {
    if (selectedLayer === index) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setSelectedLayer(null);
        setTransitionPhase("default");
        setIsAnimatingOut(false);
      }, 800);
    } else if (selectedLayer !== null) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setSelectedLayer(null);
        setTransitionPhase("default");
        setTimeout(() => {
          setSelectedLayer(index);
          setTransitionPhase("newPosition");
          setIsAnimatingOut(false);
        }, 500);
      }, 800);
    } else {
      setSelectedLayer(index);
      setTransitionPhase("newPosition");
    }
  };

  const handleLogoBoxClick = (e: React.MouseEvent) => {
    // Only reset if clicking the background Logo-Box directly
    if (e.target === e.currentTarget) {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setSelectedLayer(null);
        setTransitionPhase("default");
        setIsAnimatingOut(false);
      }, 800);
    }
  };

  const handleButtonClick = (href: string) => {
    navigate(href);
  };

  return (
    <Box
      id="Logo-Box"
      onClick={handleLogoBoxClick}
      sx={{
        position: "relative",
        width: "100%",
        height: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {layers.map((layer, index) => (
        <LogoLayer
          key={index}
          src={layer.src}
          alt={layer.alt}
          left={getAdjustedLeft(index, selectedLayer, transitionPhase, layers)}
          zIndex={layer.zIndex}
          onClick={() => handleLayerClick(index)}
          isActive={selectedLayer === index}
        />
      ))}

      {selectedLayer !== null && (
        <>
          <Circles
            isAnimatingOut={isAnimatingOut}
            left={`calc(${getAdjustedLeft(
              selectedLayer,
              selectedLayer,
              transitionPhase,
              layers
            )} + 36%)`}
            zIndex={1}
            colorIndex={layers[selectedLayer].color} // Pass the color index from the selected layer
          />
          <TextContainer
            title={layers[selectedLayer].title}
            description={layers[selectedLayer].description}
            isAnimatingOut={isAnimatingOut}
            left={`calc(${getAdjustedLeft(
              selectedLayer,
              selectedLayer,
              transitionPhase,
              layers
            )} + 36%)`}
            zIndex={20}
            onButtonClick={() => handleButtonClick(layers[selectedLayer].href)}
          />
        </>
      )}
    </Box>
  );
};

export default Logo;
